import type { ReactNode } from 'react'
import Box, { type BoxProps } from '@mui/material/Box'
import Typography, { type TypographyProps } from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import type { IconButtonProps } from '@mui/material'
import { IconButton } from 'settle-ui/components/IconButton/IconButton'

export interface IModalHeaderProps extends Omit<BoxProps, 'title'> {
  title?: ReactNode
  titleProps?: TypographyProps<'h6'>
  description?: ReactNode
  descriptionProps?: TypographyProps<'p'>
  onClose?: IconButtonProps['onClick']
}

export const ModalHeader: Settle.FC<IModalHeaderProps> = ({
  title,
  titleProps,
  description,
  descriptionProps,
  onClose,
  children,
  ...props
}) => (
  <Box
    {...props}
    sx={{
      paddingTop: 1.5,
      paddingLeft: { xs: 2, md: 3 },
      paddingRight: { xs: 2, md: 3 },
      ...props?.sx,
    }}
    data-testid="modal-header"
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 1.5,
      }}
    >
      <Box sx={{ alignSelf: 'center' }}>
        {title && (
          <Typography variant="h6" fontWeight="500" {...titleProps} data-testid="modal-header-title">
            {title}
          </Typography>
        )}
        {description && (
          <Typography
            variant="body1"
            color="onSurfaceSecondary"
            {...descriptionProps}
            data-testid="modal-header-description"
          >
            {description}
          </Typography>
        )}
      </Box>
      {onClose && (
        <IconButton size="medium" onClick={onClose} data-testid="modal-header-close-button">
          <CloseIcon />
        </IconButton>
      )}
    </Box>
    {children}
  </Box>
)
